import { ChevronDown, Sparkles, Star } from "lucide-react";
import { getPlanityUrl } from "@/config/standort";
import { useLocation, useNavigate } from "react-router-dom";
import googleLogo from "@/assets/google-logo.png";
import logo from "@/assets/22-studios-beauty-salon-logo-nuernberg.png";

const Hero = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const planityUrl = getPlanityUrl();
  const basePath = "/nuernberg";
  

  const handleScroll = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="start"
      className="relative min-h-screen flex items-end justify-center overflow-hidden pb-32"
    >
      {/* Background Video */}
      <div className="absolute inset-0 z-0">
        {/* Fallback gradient visible until video loads */}
        <div className="absolute inset-0 bg-gradient-to-br from-foreground via-charcoal to-foreground" />
        <div className="relative w-full h-full overflow-hidden">
          <iframe
            src="https://player.vimeo.com/video/1178567262?background=1&autoplay=1&loop=1&byline=0&title=0&muted=1"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.77vh] min-w-full min-h-[56.25vw] h-[100vh]"
            style={{ pointerEvents: 'none' }}
            allow="autoplay; fullscreen"
            title="Background Video"
          />
        </div>
        {/* Elegant dark overlay with warm tint */}
        <div className="absolute inset-0 bg-gradient-to-b from-foreground/40 via-foreground/50 to-foreground/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-primary/10" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute inset-0 z-[1] pointer-events-none overflow-hidden">
        {/* Corner accents */}
        <div className="absolute top-0 left-0 w-64 h-64 border-l border-t border-primary/20" />
        <div className="absolute bottom-0 right-0 w-64 h-64 border-r border-b border-primary/20" />
        
        {/* Floating decorative lines */}
        <div className="absolute top-1/4 left-8 w-px h-32 bg-gradient-to-b from-transparent via-primary/30 to-transparent animate-pulse-soft" />
        <div className="absolute top-1/3 right-8 w-px h-24 bg-gradient-to-b from-transparent via-primary/20 to-transparent animate-pulse-soft delay-500" />
      </div>

      {/* Content - Compact and lower positioned */}
      <div className="relative z-10 container px-6">
        <div className="max-w-4xl mx-auto text-center">
          {/* Main Headline */}
          <h1 className="mb-4 mt-8 md:mt-12 opacity-0 animate-fade-in-up">
            <img
              src={logo}
              alt="22 Studios – Premium Beauty Studio Nürnberg"
              className="h-32 md:h-40 lg:h-48 w-auto mx-auto brightness-0 invert drop-shadow-[0_0_25px_hsl(var(--primary)/0.8)]"
              width={300}
              height={144}
            />
          </h1>

          {/* Subheadline */}
          <p className="text-lg md:text-xl font-light text-background/80 mb-6 opacity-0 animate-fade-in-up delay-150">
            Beauty auf höchstem Niveau
          </p>

          {/* Info Tags */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-8 opacity-0 animate-fade-in-up delay-200">
            <button onClick={() => navigate(`${basePath}/haare`)} className="px-4 py-1.5 rounded-full bg-background/10 backdrop-blur-md border border-background/20 text-background text-sm font-light hover:bg-primary/20 hover:border-primary/40 transition-all duration-300 cursor-pointer">
              Hair
            </button>
            <button onClick={() => navigate(`${basePath}/facials`)} className="px-4 py-1.5 rounded-full bg-background/10 backdrop-blur-md border border-background/20 text-background text-sm font-light hover:bg-primary/20 hover:border-primary/40 transition-all duration-300 cursor-pointer">
              Skin
            </button>
            <button onClick={() => navigate(`${basePath}/facials`)} className="px-4 py-1.5 rounded-full bg-background/10 backdrop-blur-md border border-background/20 text-background text-sm font-light hover:bg-primary/20 hover:border-primary/40 transition-all duration-300 cursor-pointer">
              Lash- &amp; Brow Lift
            </button>
            <button onClick={() => navigate(`${basePath}/naegel`)} className="px-4 py-1.5 rounded-full bg-background/10 backdrop-blur-md border border-background/20 text-background text-sm font-light hover:bg-primary/20 hover:border-primary/40 transition-all duration-300 cursor-pointer">
              Nails
            </button>
            <button onClick={() => navigate(`${basePath}/haarentfernung`)} className="px-4 py-1.5 rounded-full bg-background/10 backdrop-blur-md border border-background/20 text-background text-sm font-light hover:bg-primary/20 hover:border-primary/40 transition-all duration-300 cursor-pointer">
              Dauerhafte Haarentfernung
            </button>
          </div>

          {/* Google Rating */}
          <div className="flex items-center justify-center gap-2 mb-6 opacity-0 animate-fade-in-up delay-250">
            <img src={googleLogo} alt="Google" className="w-5 h-5" />
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <span className="text-background/90 text-sm font-light">5.0 / 5.0</span>
          </div>

          {/* CTA Buttons - Compact */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 opacity-0 animate-fade-in-up delay-300">
            <button
              onClick={() => window.open(planityUrl, '_blank')}
              className="group relative px-8 py-3 bg-primary text-primary-foreground rounded-full font-medium overflow-hidden transition-all duration-500 hover:shadow-[0_0_40px_hsl(var(--primary)/0.4)] hover:scale-105"
            >
              <span className="relative z-10 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Termin buchen
              </span>
            </button>

            <button
              onClick={() => handleScroll("#haare")}
              className="group px-8 py-3 border border-background/30 rounded-full text-background font-light transition-all duration-500 hover:border-primary/50 hover:text-primary hover:bg-primary/5 backdrop-blur-sm"
            >
              <span className="flex items-center gap-2">
                Behandlungen
                <ChevronDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
              </span>
            </button>
          </div>
        </div>
      </div>


      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 opacity-0 animate-fade-in delay-500">
        <button
          onClick={() => handleScroll("#intro")}
          className="flex flex-col items-center text-background/60 hover:text-primary transition-colors duration-300 group"
          aria-label="Scroll down"
        >
          <div className="w-6 h-10 rounded-full border-2 border-current flex justify-center pt-2">
            <div className="w-1 h-2.5 bg-current rounded-full animate-bounce" />
          </div>
        </button>
      </div>
    </section>
  );
};

export default Hero;
